import { create } from "zustand";
import {
  DEFAULT_MAP_TEXT_LABEL_FONT_FAMILY,
  type MapTextLabelFontFamily,
} from "@west-marches/shared";

export interface TextLabelStyle {
  fontSize: number;
  fontFamily: MapTextLabelFontFamily;
  color: string;
  bold: boolean;
  italic: boolean;
  rotation: number;
  banner: boolean;
}

export const DEFAULT_TEXT_LABEL_STYLE: TextLabelStyle = {
  fontSize: 48,
  fontFamily: DEFAULT_MAP_TEXT_LABEL_FONT_FAMILY,
  color: "#ffffff",
  bold: false,
  italic: false,
  rotation: 0,
  banner: true,
};

export const TEXT_LABEL_COLOR_PRESETS = [
  "#ffffff",
  "#f5e6c8",
  "#1a1a1a",
] as const;

interface TextLabelStoreState {
  editingLabelId: string | null;
  editPosition: { x: number; y: number } | null;
  selectedLabelId: string | null;
  dragState: {
    startX: number;
    startY: number;
    labelStartX: number;
    labelStartY: number;
  } | null;
  rotateState: {
    startRotation: number;
    startFontSize: number;
    startHandleDistance: number;
  } | null;
  pendingStyle: TextLabelStyle;
}

interface TextLabelStoreActions {
  startCreating: (x: number, y: number) => void;
  startEditing: (id: string, x: number, y: number) => void;
  selectLabel: (id: string) => void;
  clearSelection: () => void;
  commitEdit: () => void;
  cancelEdit: () => void;
  startDrag: (
    startX: number,
    startY: number,
    labelStartX: number,
    labelStartY: number,
  ) => void;
  clearDrag: () => void;
  startRotate: (
    startRotation: number,
    startFontSize: number,
    startHandleDistance: number,
  ) => void;
  clearRotate: () => void;
  setPendingStyle: (style: Partial<TextLabelStyle>) => void;
}

type TextLabelStore = TextLabelStoreState & TextLabelStoreActions;

export const useTextLabelStore = create<TextLabelStore>((set, get) => ({
  editingLabelId: null,
  editPosition: null,
  selectedLabelId: null,
  dragState: null,
  rotateState: null,
  pendingStyle: { ...DEFAULT_TEXT_LABEL_STYLE },

  startCreating(x, y) {
    set({
      editingLabelId: "new",
      editPosition: { x, y },
      selectedLabelId: null,
      dragState: null,
      rotateState: null,
    });
  },

  startEditing(id, x, y) {
    set({
      editingLabelId: id,
      editPosition: { x, y },
      selectedLabelId: id,
      dragState: null,
      rotateState: null,
    });
  },

  selectLabel(id) {
    set({
      selectedLabelId: id,
      editingLabelId: null,
      editPosition: null,
      dragState: null,
      rotateState: null,
    });
  },

  clearSelection() {
    set({
      selectedLabelId: null,
      editingLabelId: null,
      editPosition: null,
      dragState: null,
      rotateState: null,
    });
  },

  commitEdit() {
    set({
      editingLabelId: null,
      editPosition: null,
    });
  },

  cancelEdit() {
    set({
      editingLabelId: null,
      editPosition: null,
    });
  },

  startDrag(startX, startY, labelStartX, labelStartY) {
    set({
      dragState: { startX, startY, labelStartX, labelStartY },
    });
  },

  clearDrag() {
    set({ dragState: null });
  },

  startRotate(startRotation, startFontSize, startHandleDistance) {
    set({
      rotateState: { startRotation, startFontSize, startHandleDistance },
      dragState: null,
    });
  },

  clearRotate() {
    set({ rotateState: null });
  },

  setPendingStyle(style) {
    set((s) => ({
      pendingStyle: { ...s.pendingStyle, ...style },
    }));
  },
}));
